import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

const API_URL = import.meta.env.VITE_API_URL || ''
const RETURN_TO_KEY = 'auth_return_to'

interface User {
  sub: string
  email: string
  name: string
  picture: string
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const user = ref<User | null>(null)
  // Set when the server rejects our token, so the login screen can explain why
  const sessionExpired = ref(false)
  // Set when /me couldn't be reached (network/server error); the token is kept
  const loadFailed = ref(false)
  // True while a stored token is being checked, so pages don't flash a sign-in prompt
  const loading = ref(!!token.value)

  const isLoggedIn = computed(() => !!token.value && !!user.value)

  function expireSession() {
    logout()
    sessionExpired.value = true
  }

  async function loadUser() {
    if (!token.value) return
    loadFailed.value = false
    loading.value = true
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token.value}` }
      })
      if (response.ok) {
        user.value = await response.json()
      } else if (response.status === 401) {
        expireSession()
      } else {
        loadFailed.value = true
      }
    } catch {
      // Backend unreachable: keep the token so a retry can succeed
      loadFailed.value = true
    } finally {
      loading.value = false
    }
  }

  function setToken(newToken: string) {
    token.value = newToken
    sessionExpired.value = false
    localStorage.setItem('auth_token', newToken)
    return loadUser()
  }

  function logout() {
    token.value = null
    user.value = null
    loadFailed.value = false
    localStorage.removeItem('auth_token')
  }

  /** Start Google sign-in; after login the user is sent back to returnTo (default: current page). */
  function loginWithGoogle(returnTo?: string) {
    try {
      sessionStorage.setItem(RETURN_TO_KEY, returnTo ?? window.location.pathname + window.location.search)
    } catch {
      // storage unavailable: user lands on the home page after login
    }
    window.location.href = `${API_URL}/api/auth/google`
  }

  /** The page to return to after sign-in; only same-site paths are allowed. */
  function consumeReturnTo(): string {
    let path: string | null = null
    try {
      path = sessionStorage.getItem(RETURN_TO_KEY)
      sessionStorage.removeItem(RETURN_TO_KEY)
    } catch {
      // ignore
    }
    if (!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/auth/callback')) {
      return '/'
    }
    return path
  }

  /** fetch against the API with the bearer token; a 401 logs the user out. */
  async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers)
    if (token.value) headers.set('Authorization', `Bearer ${token.value}`)
    const response = await fetch(`${API_URL}${path}`, { ...init, headers })
    if (response.status === 401) expireSession()
    return response
  }

  // Load user on init if token exists
  if (token.value) {
    loadUser()
  }

  return {
    token,
    user,
    isLoggedIn,
    sessionExpired,
    loadFailed,
    loading,
    setToken,
    logout,
    loginWithGoogle,
    consumeReturnTo,
    loadUser,
    authFetch
  }
})
